import React from 'react';
import { StageChip } from 'components/common/ui';
import { GridCellLinkV2, InstaDataGrid } from 'components/common/grid';
import {
  GRID_COLUMN_DEFAULT_PROPERTIES,
  SUBSCRIPTIONS_STATUSES,
} from '@constants';
import {
  DEFLEET_OUTCOME_NAMES,
  DEFLEET_PAGE_SIZE,
  DEFLEET_TASK_STATUSES_NAMES,
  TARGET_OPTIONS,
} from 'hooks/defleet/constants';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { boardsServices } from '@services';
import { showError } from '@utils';
import { URLS } from 'layouts/DrawerLayout/constants';

import styles from './defleetGrid.style';

const DefleetGrid = ({
  apiRef,
  defleet,
  pageCount,
  page,
  total,
  pageSize,
  refresh,
  ...props
}) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const newOptions = TARGET_OPTIONS?.map((option) => {
    return { label: option?.text, id: option?.value, styles: option?.styles };
  });

  const _renderCellLink = (params) => {
    return (
      <GridCellLinkV2 href={`/defleet/${params.value}`}>
        {params.value}
      </GridCellLinkV2>
    );
  };

  const _renderStageCell = (params) => {
    return (
      <StageChip styles={DEFLEET_TASK_STATUSES_NAMES?.[params.value]?.styles}>
        {DEFLEET_TASK_STATUSES_NAMES?.[params.value]?.label}
      </StageChip>
    );
  };

  const _renderCellStatus = (params) => {
    return SUBSCRIPTIONS_STATUSES[params?.value];
  };

  const _renderOutcomeCell = (params) => {
    const filtered = Object.keys(DEFLEET_OUTCOME_NAMES)
      .filter((key) => DEFLEET_OUTCOME_NAMES[key].label.includes(params.value))
      .reduce(
        (obj, key) => ({
          label: DEFLEET_OUTCOME_NAMES[key].label,
          styles: DEFLEET_OUTCOME_NAMES[key].styles,
        }),
        {}
      );
    return <StageChip styles={filtered?.styles}>{filtered?.label}</StageChip>;
  };

  const _renderSubscriptionIdCell = (params) => {
    if (params.value === '-' || !params.value) return params.value;
    return (
      <GridCellLinkV2 href={`/subscriptions/${params.value}`}>
        {params.value}
      </GridCellLinkV2>
    );
  };

  const _handleEdit = async (newValue, rowData) => {
    try {
      setIsSubmitting(true);
      const payload = {
        customFields: {
          [rowData.field]: newValue?.id,
        },
      };

      await boardsServices.updateTask(rowData?.id, payload);
      await refresh();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const _renderTargetCell = (params) => {
    let selectedOption = params?.value;
    if (selectedOption) {
      selectedOption = newOptions?.find((item) => params?.value === item?.id);
    }

    return (
      <Autocomplete
        value={selectedOption}
        options={newOptions}
        sx={{ ...styles.target }}
        disabled={isSubmitting}
        renderInput={(paramst) => (
          <TextField
            {...paramst}
            label="Select"
            sx={{ ...(selectedOption && styles.input(selectedOption?.styles)) }}
          />
        )}
        onChange={(e, value) => _handleEdit(value, params)}
        disableClearable
      />
    );
  };

  const columns = [
    {
      field: 'id',
      headerName: 'Defleet ID',
      ...GRID_COLUMN_DEFAULT_PROPERTIES,
      sortable: false,
      filterable: false,
      renderCell: _renderCellLink,
      width: 160,
    },
    {
      field: 'carId',
      headerName: 'Car ID',
      ...GRID_COLUMN_DEFAULT_PROPERTIES,
      sortable: false,
      filterable: false,
      width: 160,
    },
    {
      field: 'plate',
      headerName: 'Plate',
      ...GRID_COLUMN_DEFAULT_PROPERTIES,
      sortable: false,
      filterable: false,
    },
    {
      field: 'subscriptionId',
      headerName: 'Subscription ID',
      ...GRID_COLUMN_DEFAULT_PROPERTIES,
      renderCell: _renderSubscriptionIdCell,
      sortable: false,
      filterable: false,
      width: 200,
    },
    {
      field: 'brand',
      headerName: 'Brand',
      ...GRID_COLUMN_DEFAULT_PROPERTIES,
      sortable: false,
      filterable: false,
      width: 160,
    },
    {
      field: 'model',
      headerName: 'Model',
      ...GRID_COLUMN_DEFAULT_PROPERTIES,
      sortable: false,
      filterable: false,
      width: 160,
    },
    {
      field: 'edition',
      headerName: 'Edition',
      ...GRID_COLUMN_DEFAULT_PROPERTIES,
      sortable: false,
      filterable: false,
      width: 160,
    },
    {
      field: 'year',
      headerName: 'Year',
      ...GRID_COLUMN_DEFAULT_PROPERTIES,
      sortable: false,
      filterable: false,
    },
    {
      field: 'target',
      headerName: 'Target',
      ...GRID_COLUMN_DEFAULT_PROPERTIES,
      sortable: false,
      renderCell: _renderTargetCell,
      filterable: false,
      width: 140,
    },
    {
      field: 'status',
      headerName: 'Defleet Stage',
      ...GRID_COLUMN_DEFAULT_PROPERTIES,
      sortable: false,
      filterable: false,
      renderCell: _renderStageCell,
      width: 160,
    },
    {
      field: 'promisedReturnDate',
      headerName: 'Promised Return Date',
      ...GRID_COLUMN_DEFAULT_PROPERTIES,
      sortable: false,
      filterable: false,
      width: 160,
    },
    {
      field: 'returnDate',
      headerName: 'Return Date',
      ...GRID_COLUMN_DEFAULT_PROPERTIES,
      sortable: false,
      filterable: false,
      width: 160,
    },
    {
      field: 'monthlyFee',
      headerName: 'Monthly Fee',
      ...GRID_COLUMN_DEFAULT_PROPERTIES,
      sortable: false,
      filterable: false,
      width: 160,
    },
    {
      field: 'subscriptionStatus',
      headerName: 'Subscription Status',
      ...GRID_COLUMN_DEFAULT_PROPERTIES,
      renderCell: _renderCellStatus,
      sortable: false,
      filterable: false,
      width: 160,
    },
    {
      field: 'outcome',
      headerName: 'Outcome',
      ...GRID_COLUMN_DEFAULT_PROPERTIES,
      renderCell: _renderOutcomeCell,
      sortable: false,
      filterable: false,
      width: 160,
    },
  ];

  return (
    <InstaDataGrid
      apiRef={apiRef}
      columns={columns}
      rows={defleet || []}
      page={page}
      total={total}
      pageSize={pageSize}
      defaultPageSize={DEFLEET_PAGE_SIZE}
      url={URLS.defleet}
      {...props}
    />
  );
};

const memoizedDefleetGrid = React.memo(DefleetGrid);
export { memoizedDefleetGrid as DefleetGrid };

export default memoizedDefleetGrid;
